// Mint-SupplierPay › tab Đợt chi: đề xuất đợt chi theo đối tác (COST-LOGIC mục 6). Dữ liệu mẫu theo code board (`__PADUP`, `__PACOL`).
// Chứng từ đã trả bởi nguồn khác (phiếu EMP / UNC) bị chặn đưa vào đợt chi; đối tác khoá / TK đổi / chưa xác minh cũng chặn.
import type { PartnerBank, SupplierDoc, SupplierPartner } from './types';

export type DueFilter = 'all' | 'over' | 'soon';
export const DUE_FILTERS: readonly (readonly [DueFilter, string])[] = [['all', 'Tất cả'], ['over', 'Quá hạn'], ['soon', '≤ 7 ngày']];

/** Chứng từ đã được trả ở kênh khác: [loại phiếu, người trả, ngày, nội dung]. */
export const PAID_ELSEWHERE: Readonly<Record<string, readonly [string, string, string, string]>> = {
  DBN2670961: ['EMP', 'KIE•••', '21/Oct', 'phiếu EMP ODN2670966 · hoàn tiền PC26100412'],
  DBN2670963: ['BNK', 'HANHLTM', '20/Oct', 'UNC TTS26039530'],
};

export type ColState = 'over' | 'due' | 'later' | 'soa' | 'sent';
/** Thu hộ cần chuyển trả (COL): id · mã đối tác · tên · job · triệu ₫ · khoản · quy tắc · hạn · trạng thái · số ngày · UNC. */
export interface Collection { id: string; code: string; name: string; job: string; amount: number; item: string; rule: '7LV' | 'SOA'; due: string; state: ColState; days: number; unc: string }
const C = (id: string, code: string, name: string, job: string, amount: number, item: string, rule: '7LV' | 'SOA', due: string, state: ColState, days: number, unc: string): Collection => ({ id, code, name, job, amount, item, rule, due, state, days, unc });
export const COLLECTIONS: readonly Collection[] = [
  C('COL-2610-0024', 'ONE-LINE', 'ONE Vietnam', 'SL260601210', 20, 'Cược cont thu hộ hãng', '7LV', '20/Oct', 'over', 2, ''),
  C('COL-2610-0021', 'WANHAI', 'Wan Hai Lines Vietnam', 'SE260635440', 15, 'Cược cont thu hộ hãng', '7LV', '24/Oct', 'sent', 2, 'TTS26039560'),
  C('COL-2610-0026', 'TANCANGSG', 'Tân Cảng Sài Gòn', 'SL260623140', 2.6, 'Phí lưu bãi thu hộ', '7LV', '24/Oct', 'due', 2, ''),
  C('COL-2610-0019', 'NORDICFRT', 'Nordic Freight BV', 'SE260601880', 64, 'Freight collect thu hộ đại lý', 'SOA', '31/Oct', 'soa', 9, ''),
  C('COL-2610-0011', 'TRANSPAC', 'Trans-Pacific Agency', 'AE260601180', 3.2, 'Phí D/O thu hộ đại lý', 'SOA', '31/Oct', 'soa', 9, ''),
];
export const colStatus = (c: Collection): string =>
  c.state === 'over' ? `Quá hạn ${c.days} ngày` : c.state === 'due' ? `Đến hạn ${c.days} ngày` : c.state === 'later' ? 'Chưa đến hạn' : c.state === 'soa' ? 'Chờ SOA 10/2026' : 'Trong đợt chi';
/** Khoản COL được chọn vào đợt chi: không phải khoản đã vào đợt. */
export const colSelectable = (c: Collection): boolean => c.state !== 'sent';
/** "Đưa khoản đến hạn vào đợt chi": quá hạn + đến hạn + chờ SOA (trừ chưa đến hạn / đã vào đợt). */
export const colDueIds = (): string[] => COLLECTIONS.filter((c) => colSelectable(c) && c.state !== 'later').map((c) => c.id);

export const dueText = (d: Pick<SupplierDoc, 'due' | 'days'>): string =>
  d.due === 'over' ? `Quá hạn ${d.days} ngày` : d.days === 0 ? 'Đến hạn hôm nay' : d.due === 'soon' ? `Đến hạn ${d.days} ngày nữa` : `Hạn ${d.days} ngày nữa`;

export interface PayGroup {
  key: string; name: string; docs: SupplierDoc[]; dups: SupplierDoc[]; amount: number; over: number;
  bank: PartnerBank; /** chặn chọn */ blocked: boolean; needVerify: boolean; blockNote: string;
}

/** Gom chứng từ chờ chi theo đối tác (giữ thứ tự xuất hiện). `closed` = phải trả đã đóng; `q` = ô tìm; `f` = lọc hạn. */
export function buildGroups(docs: readonly SupplierDoc[], partners: readonly SupplierPartner[], f: DueFilter, q: string, closed: ReadonlySet<string> = new Set()): PayGroup[] {
  const nq = norm(q);
  let pending = docs.filter((d) => !closed.has(d.id));
  if (f === 'over') pending = pending.filter((d) => d.due === 'over');
  if (f === 'soon') pending = pending.filter((d) => d.due !== 'later');
  if (nq) pending = pending.filter((d) => norm(d.id + d.partner + d.job + d.mbl).includes(nq));
  const order: string[] = [];
  pending.forEach((d) => { if (!order.includes(d.partner)) order.push(d.partner); });
  return order.map((key) => {
    const all = pending.filter((d) => d.partner === key);
    const gd = all.filter((d) => !PAID_ELSEWHERE[d.id]);
    const p = partners.find((x) => x.key === key);
    const bank = p?.bank ?? 'ok';
    const blocked = bank === 'locked' || !gd.length;
    const needVerify = bank === 'changed' || bank === 'unverified';
    const blockNote = !gd.length ? 'Chứng từ đã được trả · chặn đưa vào đợt chi' : bank === 'locked' ? `${p?.note ?? ''} — không đưa vào đợt chi` : needVerify ? `${p?.note ?? ''} — xác nhận TK trước khi chi` : '';
    return { key, name: p?.name ?? key, docs: gd, dups: all.filter((d) => PAID_ELSEWHERE[d.id]), amount: gd.reduce((a, d) => a + d.amount, 0), over: gd.filter((d) => d.due === 'over').length, bank, blocked: blocked || needVerify, needVerify, blockNote };
  });
}

/** Lịch chi theo hạn (tính từ 22/Oct): 6 cột. */
export const CAL_BUCKETS: readonly (readonly [string, (d: SupplierDoc) => boolean, 'over' | 'soon' | 'later'])[] = [
  ['Quá hạn', (d) => d.due === 'over', 'over'],
  ['22/Oct', (d) => d.due !== 'over' && d.days === 0, 'soon'],
  ['23/Oct', (d) => d.due !== 'over' && d.days === 1, 'soon'],
  ['24/Oct', (d) => d.due !== 'over' && d.days === 2, 'soon'],
  ['25–28/Oct', (d) => d.due !== 'over' && d.days >= 3 && d.days <= 6, 'later'],
  ['Sau 28/Oct', (d) => d.due !== 'over' && d.days > 6, 'later'],
];

/** Chứng từ còn chờ chi thật (không tính đã trả ở kênh khác / đã đóng). */
export const pendingDocs = (docs: readonly SupplierDoc[], closed: ReadonlySet<string> = new Set()): SupplierDoc[] => docs.filter((d) => !closed.has(d.id) && !PAID_ELSEWHERE[d.id]);

export function calendar(docs: readonly SupplierDoc[]) {
  const raw = CAL_BUCKETS.map(([k, fn, tone]) => { const ds = docs.filter(fn); return { k, tone, n: ds.length, v: ds.reduce((a, d) => a + d.amount, 0) }; });
  const mx = Math.max(...raw.map((x) => x.v), 0.01);
  return raw.map((x) => ({ ...x, h: Math.max(2, Math.round((x.v / mx) * 72)) }));
}

/** Top 5 đối tác chờ chi nhiều nhất; đỏ nếu có chứng từ quá hạn. */
export function topPartners(docs: readonly SupplierDoc[]) {
  const by = new Map<string, { v: number; over: boolean }>();
  docs.forEach((d) => { const x = by.get(d.partner) ?? { v: 0, over: false }; x.v += d.amount; if (d.due === 'over') x.over = true; by.set(d.partner, x); });
  const arr = [...by].map(([k, x]) => ({ k, ...x })).sort((a, b) => b.v - a.v).slice(0, 5);
  const mx = Math.max(...arr.map((x) => x.v), 0.0001);
  return arr.map((x) => ({ ...x, w: Math.max(2, Math.round((x.v / mx) * 100)) }));
}

export const norm = (x: string): string => x.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').toLowerCase();
